// The sprite atlas, on WebGPU (the runtime design §4).
//
// It is the twin of the `AtlasTexture` of `gl/texture.ts`: one RGBA8 texture
// built from a 2D canvas, uploaded PREMULTIPLIED so the one blend state fits
// it, with four mip levels.
//
// `copyExternalImageToTexture` takes the canvas straight, and
// `premultipliedAlpha: true` on the destination is the
// `UNPACK_PREMULTIPLY_ALPHA_WEBGL` of the WebGL2 path. The mip chain is
// drawn rather than generated (`mips.ts`), because WebGPU has no
// `generateMipmap`.

import { ATLAS_MAX_LEVEL } from '../gl/texture';
import { ATLAS_FORMAT, MipBlitter } from './mips';
import { TEXTURE_BINDING, TEXTURE_COPY_DST, TEXTURE_RENDER_ATTACHMENT } from './flags';
import { bindUnit } from './bindgroups';
import type { AtlasTexture as AtlasTextureApi } from '../gfx/types';
import type { Gpu, GpuResource } from './context';

/** How many mip levels the atlas carries. Level 3 is the floor (R548). */
const ATLAS_LEVELS = ATLAS_MAX_LEVEL + 1;

export class GpuAtlasTexture implements GpuResource, AtlasTextureApi {
  texture: GPUTexture | null = null;
  /** The id the group-1 cache is keyed by. A new texture takes a new one. */
  id = 0;

  private view: GPUTextureView | null = null;
  private source: HTMLCanvasElement;
  private readonly mips = new MipBlitter();

  constructor(private readonly gpu: Gpu, source: HTMLCanvasElement) {
    this.source = source;
    gpu.register(this);
  }

  /** Points the texture at another canvas, for instance after a grow. */
  setSource(source: HTMLCanvasElement): void {
    this.source = source;
    if (!this.gpu.lost) this.create(this.gpu.device);
  }

  create(device: GPUDevice): void {
    // A grow mid-pack runs inside an open frame whose draws may name the old
    // texture, so it is retired and not destroyed (R1340).
    if (this.texture) this.gpu.retire(this.texture);
    this.texture = null;
    this.destroy();
    this.mips.reset();
    const w = Math.max(1, this.source.width);
    const h = Math.max(1, this.source.height);
    const texture = device.createTexture({
      label: 'fast: atlas',
      size: [w, h],
      format: ATLAS_FORMAT,
      mipLevelCount: ATLAS_LEVELS,
      // RENDER_ATTACHMENT, because the mip levels are DRAWN into.
      usage: TEXTURE_BINDING | TEXTURE_COPY_DST | TEXTURE_RENDER_ATTACHMENT,
    });
    device.queue.copyExternalImageToTexture(
      { source: this.source },
      { texture, premultipliedAlpha: true, colorSpace: 'srgb' },
      [w, h],
    );
    this.texture = texture;
    this.view = texture.createView();
    this.id = this.gpu.textureId();
    this.mips.build(device, texture, ATLAS_LEVELS);
  }

  /**
   * Uploads one rectangle again. The badge digits use it: the face lands
   * after the first bake, and the cell is redrawn in place.
   */
  patch(canvas: HTMLCanvasElement, x: number, y: number): void {
    const texture = this.texture;
    if (!texture || this.gpu.lost) return;
    const device = this.gpu.device;
    device.queue.copyExternalImageToTexture(
      { source: canvas },
      { texture, origin: [x, y], premultipliedAlpha: true, colorSpace: 'srgb' },
      [Math.max(1, canvas.width), Math.max(1, canvas.height)],
    );
    // The whole chain again: a patched cell whose lower levels hold the old
    // ink bleeds the old ink back at distance.
    this.mips.build(device, texture, ATLAS_LEVELS);
  }

  destroy(): void {
    if (this.texture) this.texture.destroy();
    this.texture = null;
    this.view = null;
    this.id = 0;
  }

  bind(unit: number): void {
    if (!this.view || this.gpu.lost) return;
    bindUnit(this.gpu.units, unit, this.view, this.id);
  }

  dispose(): void { this.gpu.unregister(this); }
}
